import { Result, backendApi } from "./backendApi";
import { VehicleTypeModel } from "./vehicleFileAPI";

export interface ModelInfoModel {
  [key: string]: any;
  recId: number | undefined;
  makeCode: string;
  model: string;
  modelSeries: string[];
  modelTypes: string[];
  modelVariants: string[];
  regionId: number | undefined;
  roofInfoTypeId: number | undefined;
  vehicleLengths: number[];
  modelInfo: string;
  extraModelInfo: string;
  fuelTypeId: number | undefined;
  engine: EngineViewModel;
  bodyStyleId: number | undefined;
  bodyStyleInfoId: number | undefined;
  monthStart: number;
  yearStart: number;
  monthEnd: number;
  yearEnd: number;
  tradeNames: string[];
  manufacturerMakeCode: string;
  displayMakeCode: string;
  datNumbers: string[];
  copiedFromRecId?: number | undefined;
  isAllModelVariants: boolean;
}

export interface ModelInfoViewModel extends ModelInfoModel {
  hasHsnTsnValues?: boolean;
}

export interface EngineViewModel {
  hybridTypeId: number | undefined;
  cylinderCount: number;
  engineSize: number;
  engineCodes: string[];
  drivetrainMaximumKws: number[];
  drivetrainNominalKws: number[];
  engineMaximumKws: number[];
  engineNominalKws: number[];
  engineFrontAxle1MaximumKws: number[];
  engineFrontAxle1NominalKws: number[];
  engineFrontAxle2MaximumKws: number[];
  engineFrontAxle2NominalKws: number[];
  engineRearAxle1MaximumKws: number[];
  engineRearAxle1NominalKws: number[];
  engineRearAxle2MaximumKws: number[];
  engineRearAxle2NominalKws: number[];
  mildHybridMaximumKws: number[];
  mildHybridNominalKws: number[];
  electric1MaximumKws: number[];
  electric1NominalKws: number[];
  electric2MaximumKws: number[];
  electric2NominalKws: number[];
  electric3MaximumKws: number[];
  electric3NominalKws: number[];
  electric4MaximumKws: number[];
  electric4NominalKws: number[];
}

class GetModelInfoRequest {
  vehicleFileNumber?: string;
}

class GetModelInfoResponse {
  modelInfos?: ModelInfoViewModel[];
  type?: VehicleTypeModel;
}

class UpdateModelInfoRequest {
  vehicleFileNumber?: string;
  modelInfos?: ModelInfoModel[];
}

class UpdateModelInfoResponse {
  recIds?: number[];
}

export enum ComponentFlagModel {
  Standard = 1,
  Optional = 2,
  NotAvailable = 3,
}

export const ComponentFlags = Object.values(ComponentFlagModel)
  .map((item) => Number(item))
  .filter((item) => !isNaN(item));

export interface VehicleComponentsModel {
  recId: number;
  description: string;
  components: { [key: string]: number };
  isNew: boolean;
}

class GetVehicleComponentsRequest {
  constructor(public vehicleFileNumber: string) {}
}

export class GetVehicleComponentsResponse {
  constructor(
    public vehicles: VehicleComponentsModel[],
    public allComponents: string[],
    public filteredComponents: string[],
    public oldComponents: string[],
    public newComponents: string[],
    public newComponentsValidated: boolean,
  ) {}
}

//TODO: #10011 there is a mess with VehicleComponentsModel and VehicleComponentModel (notice the 's').
// For backwards compatibility with the old builder we need to keep VehicleComponentModel but as soon as we can we should replace it to avoid using the funny string-based format.
class VehicleComponentModel {
  constructor(
    public recId: number,
    public componentListString: string,
  ) {}
}

class UpdateVehicleComponentsRequest {
  constructor(
    public vehicleFileNumber: string,
    public components: VehicleComponentModel[],
  ) {}
}

export const modelInfoApi = {
  convertToComponentString(vehicle: VehicleComponentsModel): string {
    const componentFlagToString = (flag: ComponentFlagModel): string => {
      switch (flag) {
        case ComponentFlagModel.Standard:
          return "S";
        case ComponentFlagModel.Optional:
          return "O";
      }

      throw new Error(
        `Component flag "${flag}" is invalid or cannot be used in the API.`,
      );
    };

    const allComponents = Object.keys(vehicle.components)
      .filter(
        (c) =>
          vehicle.components[c] === ComponentFlagModel.Standard ||
          vehicle.components[c] === ComponentFlagModel.Optional,
      )
      .map((c) => `[${componentFlagToString(vehicle.components[c])}]-${c};`);

    return allComponents.join("");
  },

  async updateVehicleComponents(
    vehicleFileNumber: string,
    vehicles: VehicleComponentsModel[],
  ): Promise<Result<any>> {
    const processedComponents = vehicles.map((v) => {
      return new VehicleComponentModel(
        v.recId,
        this.convertToComponentString(v),
      );
    });

    return backendApi.sendAsync(
      "UpdateVehicleComponents",
      new UpdateVehicleComponentsRequest(
        vehicleFileNumber,
        processedComponents,
      ),
    );
  },

  async getVehiclesWithComponents(
    vehicleFileNumber: string,
  ): Promise<Result<GetVehicleComponentsResponse>> {
    return await backendApi.sendAndGetResponseAsync<GetVehicleComponentsResponse>(
      "GetVehicleComponents",
      { vehicleFileNumber: vehicleFileNumber } as GetVehicleComponentsRequest,
    );
  },

  async getModelInfo(
    vehicleFileNumber?: string,
  ): Promise<Result<ModelInfoViewModel[]>> {
    let response =
      await backendApi.sendAndGetResponseAsync<GetModelInfoResponse>(
        "GetModelInfo",
        {
          vehicleFileNumber: vehicleFileNumber,
        } as GetModelInfoRequest,
      );

    if (response.ok) {
      return { ok: true, value: response.value.modelInfos ?? [] };
    }

    return response;
  },

  async updateModelInfo(
    vehicleFileNumber: string,
    modelInfos: ModelInfoModel[],
  ): Promise<Result<number[]>> {
    let response =
      await backendApi.sendAndGetResponseAsync<UpdateModelInfoResponse>(
        "UpdateModelInfo",
        {
          vehicleFileNumber: vehicleFileNumber,
          modelInfos: modelInfos,
        } as UpdateModelInfoRequest,
      );

    if (response.ok) {
      return { ok: true, value: response.value.recIds ?? [] };
    }

    return response;
  },
};
